import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Center, Loader, Stack, Text } from '@mantine/core';
import useTemplates from 'src/data-capture/hooks/templates/useTemplates';
import {
  MeetingNoteStatus,
  useCalendarEventMeetingNote,
} from 'src/meetings/hooks/useCalendarEventMeetingNote';
import { useGenerateCalendarEventMeetingNote } from 'src/meetings/hooks/useGenerateCalendarEventMeetingNote';
import type { CalendarEvent } from 'src/meetings/models';
import PageLoader from 'src/UI/loader/PageLoader';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { GradientBorderedInfoCard } from 'src/common/components/card/GradientBorderedInfoCard';
import { ErrorCard } from 'src/meetings/components/ErrorCard';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import { useFileUploadActions } from 'src/data-capture/hooks/file-uploads/useFileUploadActions';
import type { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { SourceTypeSelection } from './meeting-note/SourceTypeSelection';
import { TemplateSelection } from './meeting-note/TemplateSelection';
import { AgendaSelection } from './meeting-note/AgendaSelection';
import { MeetingNoteContent } from './meeting-note/MeetingNoteContent';
import { DOCUMENT_MIME_TYPES, type SourceType } from './meeting-note/types';
import { useSubstantiateMeetingNote } from 'src/meetings/hooks/useSubstantiateMeetingNote';

const filterDocumentFiles = (files: UploadedFile[]): UploadedFile[] => {
  return files.filter(file => DOCUMENT_MIME_TYPES.includes(file.file.fileType));
};

type Props = {
  event: CalendarEvent;
};

export const MeetingNoteTab = ({ event }: Props) => {
  const [forceShowOptions, setForceShowOptions] = useState(false);
  const [sourceType, setSourceType] = useState<SourceType | undefined>(undefined);
  const [selectedAgendaFileId, setSelectedAgendaFileId] = useState<string | undefined>(undefined);
  const [isUploading, setIsUploading] = useState(false);
  const [summarise, setSummarise] = useState(true);

  const {
    flagEnabledMeetingNoteFromAgenda,
    flagEnabledMeetingNoteSummarisation,
    flagEnabledMeetingNoteClaims,
  } = useFlags();

  const clientId = useMemo(() => {
    for (const participant of event.eventParticipants) {
      if (participant.participant.client) {
        return participant.participant.client.id;
      }
    }
    return undefined;
  }, [event.eventParticipants]);

  const { files: templates, isLoading: isLoadingTemplates } = useTemplates({
    templateType: 'MEETING_NOTE',
  });

  const { files: clientFiles, isLoading: isLoadingFiles } = useFileUploads({
    clientId,
  });
  const { uploadFiles } = useFileUploadActions({ clientId });

  const agendaFiles = useMemo(() => filterDocumentFiles(clientFiles), [clientFiles]);

  const {
    meetingNote,
    isLoading: isLoadingNote,
    isError,
    error,
    refetch,
  } = useCalendarEventMeetingNote(event.id, {
    enabled: event.hasTranscription,
  });

  const { generateMeetingNote, isLoading: isGenerating } = useGenerateCalendarEventMeetingNote(
    event.id,
  );

  const { substantiate, isSubstantiating } = useSubstantiateMeetingNote(clientId ?? '', [
    ['calendar-events', event.id, 'meeting-notes'],
  ]);
  const hasSubstantiatedRef = useRef(false);

  useEffect(() => {
    if (
      flagEnabledMeetingNoteClaims &&
      clientId &&
      meetingNote?.id &&
      meetingNote.status === MeetingNoteStatus.READY &&
      !meetingNote.taggedMarkdownContent &&
      !hasSubstantiatedRef.current &&
      !isSubstantiating
    ) {
      hasSubstantiatedRef.current = true;
      substantiate(meetingNote.id).catch(() => {
        hasSubstantiatedRef.current = false;
      });
    }
  }, [
    flagEnabledMeetingNoteClaims,
    clientId,
    meetingNote?.id,
    meetingNote?.status,
    meetingNote?.taggedMarkdownContent,
    isSubstantiating,
    substantiate,
  ]);

  const handleSelectTemplate = async (templateId: string) => {
    try {
      setForceShowOptions(false);
      setSourceType(undefined);
      await generateMeetingNote({ templateId, summarise });
      refetch();
    } catch (error) {
      notifyAndLogError('Failed to generate meeting note', 'Please try again later', error, {
        eventId: event.id,
        templateId,
      });
    }
  };

  const handleSelectAgenda = async (agendaFileId: string) => {
    try {
      setForceShowOptions(false);
      setSourceType(undefined);
      setSelectedAgendaFileId(undefined);
      await generateMeetingNote({ agendaFileId, summarise });
      refetch();
    } catch (error) {
      notifyAndLogError('Failed to generate meeting note', 'Please try again later', error, {
        eventId: event.id,
        agendaFileId,
      });
    }
  };

  const handleFileUpload = async (uploadEvent: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = uploadEvent.target.files;
    if (fileList && clientId) {
      setIsUploading(true);
      try {
        const payload = Array.from(fileList).map(file => ({
          file,
          isResolved: true,
        }));
        const result = await uploadFiles(payload);
        const firstResult = result[0];
        if (firstResult) {
          setSelectedAgendaFileId(firstResult.data.id);
        }
      } catch (error) {
        notifyAndLogError(
          'Failed to upload file',
          'An error occurred while uploading the agenda file. Please try again.',
          error,
        );
      }
      setIsUploading(false);
    }
    uploadEvent.target.value = '';
  };

  const handleBackFromAgenda = () => {
    setSourceType(undefined);
    setSelectedAgendaFileId(undefined);
  };

  const isLoading =
    isLoadingTemplates ||
    (event.hasMeetingNote && isLoadingNote) ||
    (flagEnabledMeetingNoteFromAgenda && sourceType === 'agenda' && isLoadingFiles);

  if (isLoading) {
    return <PageLoader absolute={false} />;
  }

  const renderSelectionContent = () => {
    if (!flagEnabledMeetingNoteFromAgenda) {
      return (
        <TemplateSelection
          templates={templates}
          onSelectTemplate={handleSelectTemplate}
          summarise={summarise}
          onSummariseChange={setSummarise}
          showSummariseToggle={flagEnabledMeetingNoteSummarisation}
        />
      );
    }

    if (sourceType === 'template') {
      return (
        <TemplateSelection
          templates={templates}
          onSelectTemplate={handleSelectTemplate}
          onBack={() => setSourceType(undefined)}
          summarise={summarise}
          onSummariseChange={setSummarise}
          showSummariseToggle={flagEnabledMeetingNoteSummarisation}
        />
      );
    }

    if (sourceType === 'agenda') {
      return (
        <AgendaSelection
          agendaFiles={agendaFiles}
          selectedAgendaFileId={selectedAgendaFileId}
          isUploading={isUploading}
          isGenerating={isGenerating}
          onSelectFile={setSelectedAgendaFileId}
          onFileUpload={handleFileUpload}
          onGenerate={() => selectedAgendaFileId && handleSelectAgenda(selectedAgendaFileId)}
          onBack={handleBackFromAgenda}
          summarise={summarise}
          onSummariseChange={setSummarise}
          showSummariseToggle={flagEnabledMeetingNoteSummarisation}
        />
      );
    }

    return <SourceTypeSelection onSelectSourceType={setSourceType} />;
  };

  if (isError && event.hasMeetingNote) {
    return (
      <Center h="100%">
        <Stack align="center" gap="md" maw={400}>
          <Text size="sm" c="red.7" ta="center" fw={FONT_WEIGHTS.semibold}>
            Unable to load meeting note
          </Text>
          <Text size="sm" c="gray.7" ta="center">
            {(error instanceof Error ? error.message : null) ||
              'An error occurred while loading the meeting note. Please contact support if this persists.'}
          </Text>
        </Stack>
      </Center>
    );
  }

  if (meetingNote && !isGenerating) {
    if (meetingNote.status === MeetingNoteStatus.READY) {
      if (!meetingNote.markdownContent || !meetingNote.templatisedFileId) {
        return (
          <Center h="100%">
            <Stack align="center" gap="md" maw={400}>
              <Text size="sm" c="red.7" ta="center" fw={FONT_WEIGHTS.semibold}>
                Meeting note data unavailable
              </Text>
              <Text size="sm" c="gray.7" ta="center">
                The meeting note appears to be incomplete. Please contact support for assistance.
              </Text>
            </Stack>
          </Center>
        );
      }

      if (isSubstantiating) {
        return (
          <GradientBorderedInfoCard leftIcon={<Loader size="24px" />}>
            Substantiating claims
          </GradientBorderedInfoCard>
        );
      }

      return (
        <MeetingNoteContent
          markdownContent={meetingNote.taggedMarkdownContent ?? meetingNote.markdownContent}
          fileId={meetingNote.templatisedFileId}
        />
      );
    }

    if (meetingNote.status === MeetingNoteStatus.MEETING_NOTE_PROCESSING) {
      return (
        <GradientBorderedInfoCard leftIcon={<Loader size="24px" />}>
          Generating the meeting note
        </GradientBorderedInfoCard>
      );
    }

    if (meetingNote.status === MeetingNoteStatus.TRANSCRIPTION_PROCESSING) {
      return (
        <GradientBorderedInfoCard>
          {"Your recording is being transcribed. You'll be able to generate a note once ready."}
        </GradientBorderedInfoCard>
      );
    }

    if (meetingNote.status === MeetingNoteStatus.FAILED) {
      return forceShowOptions ? (
        renderSelectionContent()
      ) : (
        <ErrorCard>
          <Text ml={'xxs'} size="sm" fw={600}>
            Unable to generate the meeting note, please{' '}
            <Text
              span
              c={'brand.8'}
              size="sm"
              fw={600}
              style={{ cursor: 'pointer' }}
              onClick={async () => {
                if (meetingNote?.templateId) {
                  await handleSelectTemplate(meetingNote?.templateId);
                }
              }}
            >
              try again
            </Text>{' '}
            or{' '}
            <Text
              span
              c={'brand.8'}
              size="sm"
              fw={600}
              style={{ cursor: 'pointer' }}
              onClick={() => setForceShowOptions(true)}
            >
              select another option
            </Text>
          </Text>
        </ErrorCard>
      );
    }

    if (meetingNote.status === MeetingNoteStatus.NOT_CREATED) {
      return renderSelectionContent();
    }
  }

  if (isGenerating) {
    return (
      <GradientBorderedInfoCard leftIcon={<Loader size="24px" />}>
        Generating the meeting note
      </GradientBorderedInfoCard>
    );
  }

  if (!event.hasTranscription) {
    return (
      <Box p="md">
        <Text size="sm" c="gray.7">
          A transcription is required to generate a meeting note.
        </Text>
      </Box>
    );
  }

  return renderSelectionContent();
};
